import React, { useState } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import EmbeddedClinicList from "../../components/EmbeddedClinicList";
import {
  ThreeColRow,
  StepCard,
  StepCardIcon,
  StepCardTitle,
  StepCardDesc,
  TwoColContentRow,
  ColBullets,
  ColImageRight,
  ColImg,
  SectionBottomImage,
} from "../../components/ContentLayouts";

// ── Icon imports ───────────────────────────────────────────────────────────────
import iconStep1 from "../../assets/images/icons/location.png";
import iconStep2 from "../../assets/images/icons/review-list.png";
import iconStep3 from "../../assets/images/icons/med-calendar.png";

// ── Image imports ───────────────────────────────────────────────────────────────
import imgGroomingNearMe from "../../assets/images/content/pet-grooming/grooming-nearme.jpg";
import imgGroomingDog from "../../assets/images/content/pet-grooming/grooming-dog.jpg";
import imgGroomingCat from "../../assets/images/content/pet-grooming/grooming-cat.jpg";

const faqs = [
  {
    question: "What grooming services do you offer for pets?",
    answer:
      "Through PetQura, you can find pet grooming near you offering bathing, brushing, de-matting, nail trimming, ear cleaning, coat trims, and spa-style add-ons like de-shedding treatments, all searchable by pet grooming center near me listings on our platform.",
  },
  {
    question: "How often should I groom my pet?",
    answer:
      "This depends on coat type and breed, but most dogs and cats benefit from professional grooming every four to six weeks, with brushing at home in between.",
  },
  {
    question: "Why is professional pet grooming important?",
    answer:
      "Professional grooming keeps your pet clean and comfortable while also giving groomers a chance to spot skin issues, parasites, or lumps early, something that's easy to miss at home.",
  },
  {
    question: "Can grooming help improve my pet's health?",
    answer:
      "Yes. Regular grooming reduces the risk of skin infections, ear infections, and matting, while also keeping nails and paws in good condition, which supports your pet's overall health.",
  },
  {
    question: "Do you groom puppies and kittens?",
    answer:
      "Many clinics listed on PetQura groom puppies and kittens once they're old enough, usually after their first round of vaccinations.",
  },
  {
    question: "How do I find pet grooming near me on PetQura?",
    answer:
      "Simply search for pet grooming near you and select the grooming service your pet needs. PetQura will show you pet grooming nearby in Bangalore along with their availability, so you can book directly.",
  },
  {
    question: "Is pet grooming home service available through PetQura?",
    answer:
      "Our platform connects you with the closest veterinary clinics for grooming appointments, so you know exactly where your pet will be groomed and by whom.",
  },
  {
    question: "Do I need to bring anything for the appointment?",
    answer:
      "Most clinics provide everything needed. If your pet has specific product preferences or sensitivities, mention this when you book.",
  },
];

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <FaqRow>
      <FaqQuestion onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <FaqIcon>{open ? "−" : "+"}</FaqIcon>
      </FaqQuestion>
      {open && <FaqAnswer>{answer}</FaqAnswer>}
    </FaqRow>
  );
};

const PetGrooming = () => {
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Pet Grooming Near You in Bangalore | PetQura</title>
        <meta
          name="description"
          content="Find pet grooming near you or book pet grooming services at home in Bangalore. From baths and haircuts to full pet spa treatments, discover trusted groomers on PetQura."
        />
      </Helmet>

      <PageWrapper>
        <HeroSection>
          <h1>Pet Grooming and Spa Services in Bangalore</h1>
          <EmbeddedClinicList serviceId={null} previewMode />
        </HeroSection>

        {/* ── Intro ── */}
        <ContentSection>
          <SectionHeading>Pet Grooming Services by PetQura</SectionHeading>
          <p>
            Typing <em>pet grooming near me</em> into a search bar at 11 p.m.
            because your dog just rolled in something questionable is a very
            specific kind of stress. We built PetQura to take that stress off
            your plate. Tell us what your pet needs and where you are in
            Bangalore, and we'll show you the closest veterinary clinics
            offering that exact grooming service. Finding pet grooming near you
            shouldn't take an hour of scrolling, so we've kept it to a few
            taps: pick a service, see nearby clinics, and book. That's it.
          </p>
          <SectionBottomImage
            src={imgGroomingNearMe}
            alt="Finding pet grooming near you in Bangalore made simple"
          />
        </ContentSection>

        {/* ── Grooming Services ── */}
        <ContentSection>
          <SectionHeading>
            Pet Grooming Services in Bangalore, Made Simple
          </SectionHeading>
          <p>
            Every pet has different grooming needs, and those needs change with
            age, breed, and season. That's why we've built PetQura around
            choice. Search for pet grooming services in Bangalore on our
            platform, and you'll see clinics near you that offer the exact
            service your pet needs, along with their timings and location. You
            pick what works, and we handle the rest of the legwork.
          </p>
          <p>
            Here's what you'll typically find covered under pet grooming
            Bangalore listings on PetQura:
          </p>
          <BulletList>
            <li>Bathing and blow-drying with pet-safe shampoos</li>
            <li>Brushing and de-matting for tangle-free coats</li>
            <li>Nail trimming and paw pad care</li>
            <li>Ear cleaning and hygiene trims</li>
            <li>Coat trimming and breed-specific styling</li>
          </BulletList>
        </ContentSection>

        {/* ── How to Book — Pattern C (3-col steps) ── */}
        <ContentSection>
          <SectionHeading>
            How PetQura Helps You Book a Grooming Appointment
          </SectionHeading>
          <p>
            Booking shouldn't take longer than the grooming session itself:
          </p>
          <ThreeColRow>
            <StepCard>
              <StepCardIcon src={iconStep1} alt="Enter your location" />
              <StepCardTitle>Enter Location &amp; Service</StepCardTitle>
              <StepCardDesc>
                Enter your desired location and select the grooming service
                your pet needs.
              </StepCardDesc>
            </StepCard>
            <StepCard>
              <StepCardIcon src={iconStep2} alt="Review clinics" />
              <StepCardTitle>Review Nearby Clinics</StepCardTitle>
              <StepCardDesc>
                See a list of veterinary clinics near you that offer the
                service and compare their availability.
              </StepCardDesc>
            </StepCard>
            <StepCard>
              <StepCardIcon src={iconStep3} alt="Confirm appointment" />
              <StepCardTitle>Pick a Slot &amp; Confirm</StepCardTitle>
              <StepCardDesc>
                Choose a time that suits you and confirm your appointment in
                a few taps.
              </StepCardDesc>
            </StepCard>
          </ThreeColRow>
          <p>
            We built it this way because pet parents already have enough on
            their plates without an appointment-booking headache added to it.
          </p>
        </ContentSection>

        {/* ── Pet Spa ── */}
        <ContentSection>
          <SectionHeading>Pet Spa Near You</SectionHeading>
          <p>
            Grooming isn't only about looking tidy. A lot of it comes down to
            comfort and skin health. Through PetQura, you can find pet spa near
            you options at clinics across Bangalore that go a step further than
            a basic wash and trim.
          </p>
          <p>Spa-style add-ons you'll come across include:</p>
          <BulletList>
            <li>Deep-conditioning baths for dry or sensitive skin</li>
            <li>
              De-shedding treatments to cut down on loose fur at home
            </li>
            <li>
              Paw balm and pad moisturising for pets who walk a lot
            </li>
            <li>Breed-specific styling and finishing touches</li>
            <li>Calming, low-stress handling for anxious pets</li>
          </BulletList>
          <p>
            Finding a pet spa near you shouldn't mean settling for the first
            clinic that shows up in a random search. PetQura lets you see what
            each nearby clinic actually offers before you commit, so you know
            your pet is getting the right kind of care and not just a rushed
            bath.
          </p>
        </ContentSection>

        {/* ── Why Grooming Matters for Dogs ── */}
        <ContentSection>
          <SectionHeading>Why Grooming Matters for Dogs</SectionHeading>
          <p>
            Dogs rely on regular grooming far more than most pet parents
            realise. Coat type, activity level, and Bangalore's mix of dust and
            humidity all play a part in how often a dog needs professional
            attention.
          </p>
          <p>Grooming for dogs helps with:</p>
          <TwoColContentRow>
            <ColBullets>
              <BulletList>
                <li>
                  Preventing skin infections caused by trapped dirt, moisture,
                  or matted fur
                </li>
                <li>
                  Reducing shedding around the house through regular brushing
                  and de-shedding
                </li>
                <li>
                  Avoiding overgrown nails that affect posture and walking
                </li>
                <li>
                  Keeping ears clean and dry to lower the risk of infections
                </li>
                <li>
                  Supporting overall comfort, especially for double-coated or
                  long-haired breeds
                </li>
              </BulletList>
            </ColBullets>
            <ColImageRight>
              <ColImg
                src={imgGroomingDog}
                alt="Why grooming matters for dogs"
              />
            </ColImageRight>
          </TwoColContentRow>
          <p>
            A consistent grooming routine, booked through pet grooming
            Bangalore listings on PetQura, keeps these small issues from
            turning into vet visits for something more serious.
          </p>
        </ContentSection>

        {/* ── Why Grooming Matters for Cats ── */}
        <ContentSection>
          <SectionHeading>Why Grooming Matters for Cats</SectionHeading>
          <p>
            Cats are famously tidy on their own, which is why pet parents
            sometimes assume they don't need professional grooming at all. In
            practice, a bit of outside help goes a long way, especially for
            long-haired cats or older ones.
          </p>
          <p>Grooming for cats helps with:</p>
          <TwoColContentRow>
            <ColBullets>
              <BulletList>
                <li>
                  Preventing painful mats and tangles, especially around the
                  belly and legs
                </li>
                <li>Reducing hairballs by clearing out loose, excess fur</li>
                <li>
                  Keeping nails at a safe length so scratching stays
                  comfortable, not risky
                </li>
                <li>
                  Spotting skin issues, lumps, or parasites early during a
                  hands-on session
                </li>
                <li>
                  Making the experience calmer for cats who find handling
                  stressful
                </li>
              </BulletList>
            </ColBullets>
            <ColImageRight>
              <ColImg
                src={imgGroomingCat}
                alt="Why grooming matters for cats"
              />
            </ColImageRight>
          </TwoColContentRow>
          <p>
            Regular grooming also gives clinics near you a chance to notice
            small health changes before they become bigger problems, which is
            reason enough to book a pet grooming near you even for the most
            self-sufficient cat.
          </p>
        </ContentSection>

        {/* ── FAQ ── */}
        <ContentSection>
          <SectionHeading>Frequently Asked Questions (FAQs)</SectionHeading>
          <FaqList>
            {faqs.map((faq, idx) => (
              <FaqItem key={idx} question={faq.question} answer={faq.answer} />
            ))}
          </FaqList>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default PetGrooming;

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 5% 5rem;

  @media (max-width: 768px) {
    padding: 2rem 5% 4rem;
  }
`;

const HeroSection = styled.section`
  margin-bottom: 3rem;

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 1.5rem;
    font-family: Montserrat, sans-serif;
    text-align: center;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
`;

const ContentSection = styled.section`
  margin-bottom: 2.5rem;

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 1rem;
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1rem;
  font-family: Montserrat, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BulletList = styled.ul`
  padding-left: 1.4rem;
  margin-bottom: 1rem;

  li {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 0.6rem;
  }
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
`;

const FaqRow = styled.div`
  border-bottom: 1px solid #e5e5e5;

  &:last-child {
    border-bottom: none;
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: none;
  padding: 1rem 1.25rem;
  text-align: left;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  font-family: Montserrat, sans-serif;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f7f9ff;
  }
`;

const FaqIcon = styled.span`
  font-size: 20px;
  font-weight: 400;
  color: #00419d;
  flex-shrink: 0;
  margin-left: 1rem;
`;

const FaqAnswer = styled.div`
  padding: 0 1.25rem 1rem;
  font-size: 15px;
  line-height: 1.75;
  color: #555555;
  font-family: Montserrat, sans-serif;
  background: #fafafa;
`;
